import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { FolderItem } from "@titancloud/shared";
import { formatRelativeDate } from "../../utils/dateFormat";
import { useExplorerStore } from "../../store/explorerStore";
import { usePrefetchFolder } from "../../hooks/usePrefetchFolder";

interface FolderRowProps {
  folder: FolderItem;
  isRenaming: boolean;
  onRenameComplete: (name: string) => void;
  onRenameCancel: () => void;
  /** Admin read-only explorer: no selection, custom navigation */
  readOnly?: boolean;
  onFolderNavigate?: (folderId: string) => void;
}

const LONG_PRESS_MS = 500;

export function FolderRow({
  folder,
  isRenaming,
  onRenameComplete,
  onRenameCancel,
  readOnly,
  onFolderNavigate,
}: FolderRowProps) {
  const [renameValue, setRenameValue] = useState(folder.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const navigate = useNavigate();
  const { selectedIds, toggleSelect, selectionMode, enterSelectionMode, setCurrentFolder } = useExplorerStore();
  const isSelected = selectedIds.has(folder.id);
  // The admin explorer reads through its own endpoints, so only the user's own folders prefetch.
  const prefetchHandlers = usePrefetchFolder(folder.id);
  const folderPrefetch = readOnly ? {} : prefetchHandlers;

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && renameValue.trim()) onRenameComplete(renameValue.trim());
    else if (e.key === "Escape") onRenameCancel();
  };

  const startLongPress = () => {
    if (readOnly) return;
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      enterSelectionMode();
      toggleSelect(folder.id);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleClick = () => {
    if (isRenaming) return;
    if (didLongPress.current) { didLongPress.current = false; return; }
    if (readOnly && onFolderNavigate) {
      onFolderNavigate(folder.id);
      return;
    }
    if (selectionMode) {
      toggleSelect(folder.id);
    } else {
      setCurrentFolder(folder.id);
      navigate(`/app/folder/${folder.id}`);
    }
  };

  const checkboxVisible = !readOnly && (selectionMode || isSelected);

  return (
    <div
      className={`group flex items-center gap-3 border-b border-gray-800/50 px-4 py-2.5 text-sm transition-colors cursor-pointer select-none ${
        isSelected ? "bg-brand-900/30" : "hover:bg-gray-800/50"
      }`}
      onClick={handleClick}
      {...folderPrefetch}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onTouchMove={cancelLongPress}
    >
      {/* Checkbox — spacer in read-only admin explorer */}
      {readOnly ? (
        <div className="h-4 w-4 flex-shrink-0" aria-hidden />
      ) : (
        <div
          className={`flex-shrink-0 transition-opacity ${
            checkboxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          onClick={(e) => { e.stopPropagation(); toggleSelect(folder.id); }}
        >
          <div className={`flex h-4 w-4 items-center justify-center rounded border ${
            isSelected ? "border-indigo-500 bg-indigo-500" : "border-gray-600 bg-transparent"
          }`}>
            {isSelected && (
              <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 8" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Folder icon */}
      <svg className="h-5 w-5 flex-shrink-0 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
      </svg>

      {/* Name + mobile date */}
      <div className="min-w-0 flex-1">
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={onRenameCancel}
            className="w-full rounded border border-brand-500 bg-gray-800 px-2 py-0.5 text-sm text-gray-100 focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="block truncate font-medium text-gray-200 hover:text-white">
              {folder.name}
            </span>
            {/* Mobile: item count · date as secondary line */}
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 sm:hidden">
              <span>{folder.childCount} item{folder.childCount !== 1 ? "s" : ""}</span>
              <span className="text-gray-700">·</span>
              <span>{formatRelativeDate(folder.createdAt)}</span>
            </div>
          </>
        )}
      </div>

      {/* Desktop columns */}
      <div className="hidden w-24 flex-shrink-0 text-right text-gray-500 sm:block">
        {folder.childCount} item{folder.childCount !== 1 ? "s" : ""}
      </div>
      <div className="hidden w-36 flex-shrink-0 text-right text-gray-500 sm:block">
        {formatRelativeDate(folder.createdAt)}
      </div>
      <div className="w-8 flex-shrink-0" />
    </div>
  );
}
